import { PDFDocument, PDFPage, degrees, rgb, StandardFonts } from 'pdf-lib'

export interface PdfSection {
  name: string
  blob: Blob
  startOnFrontPage?: boolean
  tocLabel?: string
  rotateLandscapeToPortrait?: boolean
}

interface TocEntry {
  label: string
  pageNumber: number
}

const createBlankPage = async (mergedPdf: PDFDocument): Promise<PDFPage> => {
  const page = mergedPdf.addPage([612, 792])
  return page
}

const addPageNumbers = (pages: PDFPage[]): void => {
  pages.forEach((page, index) => {
    const pageNumber = index + 1
    const { width, height } = page.getSize()
    const rotation = page.getRotation().angle

    if (rotation === 270) {
      page.drawText(`${pageNumber}`, {
        x: 30,
        y: 50,
        size: 10,
        color: rgb(0.5, 0.5, 0.5),
        rotate: degrees(270)
      })
    } else {
      page.drawText(`${pageNumber}`, {
        x: width - 50,
        y: 30,
        size: 10,
        color: rgb(0.5, 0.5, 0.5)
      })
    }
  })
}

const generateTocPage = async (mergedPdf: PDFDocument, entries: TocEntry[]): Promise<PDFPage> => {
  const page = mergedPdf.addPage([612, 792])
  const helveticaBold = await mergedPdf.embedFont(StandardFonts.HelveticaBold)
  const helvetica = await mergedPdf.embedFont(StandardFonts.Helvetica)

  const titleFontSize = 18
  const entryFontSize = 12
  const topMargin = 120
  const leftMargin = 72
  const rightMargin = 72
  const lineSpacing = 30

  const titleText = 'Table of Contents'
  const titleWidth = helveticaBold.widthOfTextAtSize(titleText, titleFontSize)
  page.drawText(titleText, {
    x: (612 - titleWidth) / 2,
    y: 792 - topMargin,
    size: titleFontSize,
    font: helveticaBold,
    color: rgb(0.1, 0.1, 0.1)
  })

  let y = 792 - topMargin - 50
  const pageWidth = 612

  for (const entry of entries) {
    const labelWidth = helvetica.widthOfTextAtSize(entry.label, entryFontSize)
    const pageNumText = `${entry.pageNumber}`
    const pageNumWidth = helvetica.widthOfTextAtSize(pageNumText, entryFontSize)

    page.drawText(entry.label, {
      x: leftMargin,
      y,
      size: entryFontSize,
      font: helvetica,
      color: rgb(0.15, 0.15, 0.15)
    })

    page.drawText(pageNumText, {
      x: pageWidth - rightMargin - pageNumWidth,
      y,
      size: entryFontSize,
      font: helvetica,
      color: rgb(0.15, 0.15, 0.15)
    })

    const dotSpacing = 6
    const dotsStartX = leftMargin + labelWidth + 10
    const dotsEndX = pageWidth - rightMargin - pageNumWidth - 10
    let dotX = dotsStartX
    while (dotX < dotsEndX) {
      page.drawText('.', {
        x: dotX,
        y,
        size: entryFontSize,
        font: helvetica,
        color: rgb(0.6, 0.6, 0.6)
      })
      dotX += dotSpacing
    }

    y -= lineSpacing
  }

  return page
}

export const combinePdfs = async (sections: PdfSection[], includeTableOfContents = false): Promise<Blob> => {
  const mergedPdf = await PDFDocument.create()
  const allPages: PDFPage[] = []
  let currentPageCount = 0
  const tocEntries: TocEntry[] = []

  for (const section of sections) {
    try {
      if (section.startOnFrontPage && currentPageCount > 0 && currentPageCount % 2 !== 0) {
        const blankPage = await createBlankPage(mergedPdf)
        allPages.push(blankPage)
        currentPageCount++
      }

      if (section.tocLabel) {
        tocEntries.push({
          label: section.tocLabel,
          pageNumber: currentPageCount + 1
        })
      }

      const pdfBytes = await section.blob.arrayBuffer()
      const pdf = await PDFDocument.load(pdfBytes)

      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices())
      copiedPages.forEach(page => {
        if (section.rotateLandscapeToPortrait) {
          const { width, height } = page.getSize()
          if (width > height) {
            page.setRotation(degrees(270))
          }
        }
        mergedPdf.addPage(page)
        allPages.push(page)
        currentPageCount++
      })
    } catch (error) {
      console.error(`Failed to merge PDF section: ${section.name}`, error)
    }
  }

  if (includeTableOfContents && tocEntries.length > 0) {
    if (currentPageCount > 0 && currentPageCount % 2 !== 0) {
      await createBlankPage(mergedPdf)
    }

    await generateTocPage(mergedPdf, tocEntries)
  }

  addPageNumbers(allPages)

  const pdfBytes = await mergedPdf.save()
  return new Blob([pdfBytes], { type: 'application/pdf' })
}

export const downloadCombinedPdf = async (sections: PdfSection[], filename: string, includeTableOfContents = false): Promise<void> => {
  try {
    const combinedBlob = await combinePdfs(sections, includeTableOfContents)

    const url = URL.createObjectURL(combinedBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to download combined PDF:', error)
    throw error
  }
}
